import { z } from "zod";
import { useContext } from "react";
import { createCustomer, getCustomer, saveCustomer } from "../api/customer-controller";
import { ModelDefinition, ValidationMessage, validationMessage } from "../base-components/hooks/useModel";
import { Context } from "../App";
import { RoutePath } from "@/generic/config";
import { WorksiteHitSchemaWithoutCustomer } from "./worksite";
import { AttachedFileSchema } from "./attached-file.schema";
import { PropertyValueSchema } from "./property";

export const CustomerSchema = z.object({
    id: z.number(),
    externalId: z.string(),
    isCompany: z.boolean(),
    firstName: z.string(),
    lastName: z.string(),
    identificationNumber: z.string(),
    vatNumber: z.string(),
    primaryContact: z.string(),
    email: z.string().email().optional(),
    phone: z.string(),
    address: z.string(),
    postalCode: z.string(),
    city: z.string(),
    notes: z.string(),
    worksites: z.array(WorksiteHitSchemaWithoutCustomer),
    attachedFiles: z.array(AttachedFileSchema),
    propertyValues: z.array(PropertyValueSchema),
});

export type Customer = z.infer<typeof CustomerSchema>;

const ValidationMessages = (model: Customer) => {
    const { translations } = useContext(Context);
    const rules = translations.model.customer.validationRules;
    const messages: ValidationMessage<Customer>[] = [];

    if (!model.isCompany) {
        if (!model.firstName) {
            messages.push(validationMessage("firstName", rules.firstNameIsRequired, "error"));
        }

        if (!model.lastName) {
            messages.push(validationMessage("lastName", rules.lastNameIsRequired, "error"));
        }
    }

    if (model.isCompany && model.lastName) {
        messages.push(validationMessage("lastName", rules.companyCannotHaveLastName, "warning"));
    }

    if (model.isCompany && !model.primaryContact) {
        messages.push(validationMessage("primaryContact", rules.primaryContactShouldBeSetForCompany, "warning"));
    }

    if (!model.email) {
        messages.push(validationMessage("email", rules.emailIsSuggested, "warning"));
    }

    if (!model.phone) {
        messages.push(validationMessage("phone", rules.phoneIsSuggested, "warning"));
    }

    if (model.isCompany && !model.identificationNumber) {
        messages.push(validationMessage("identificationNumber", rules.companyMustHaveIdentificationNumber, "error"));
    }


    return messages;
};

const emptyCustomerObject: Customer = {
    id: 0,
    externalId: "",
    isCompany: false,
    firstName: "",
    lastName: "",
    identificationNumber: "",
    vatNumber: "",
    primaryContact: "",
    phone: "",
    address: "",
    postalCode: "",
    city: "",
    notes: "",
    worksites: [],
    attachedFiles: [],
    propertyValues: [],
};

export const CustomerModel: ModelDefinition<Customer> = {
    getById: getCustomer,
    saveFromModel: saveCustomer,
    createFromModel: createCustomer,
    validationMessages: ValidationMessages,
    routePath: RoutePath.Customers,
    typeProviderObject: CustomerSchema.parse(emptyCustomerObject),
};